import {
  InteractionRequiredAuthError,
  InteractionType,
} from '@azure/msal-browser';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import ErrorIcon from '@mui/icons-material/Error';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { loginRequest } from 'authConfig';
import { v4 as uuidv4 } from 'uuid';
import {
  LoadingSection,
  PageCard,
  PageCardRowSection,
} from 'Components/Display';
import { Layout } from 'Features/Shared/Layout';
import { User } from 'Models/SharedModels';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { asyncLogout, isNullEmptyOrUndefined, useApiWorker } from 'Utilities';
import { useToasts } from './ToastProvider';
export interface GlobalState {
  tenant?: string;
  clientId?: string;
  apiEndpoint?: string;
  redirectUri?: string;
  currentUser?: User;
  token?: string;
  versionNumber?: string;
  baseUrl: string;
  isSidebarOpen: boolean;
  isMainSidebarOpen: boolean;
  isUnauthorized?: boolean;
  environmentName?: string;
  sessionId?: string;
  permissionsV3?: Set<string>;
}

export enum GlobalActionTypes {
  SetUser = 'setUser',
  SetToken = 'setToken',
  ToggleSidebar = 'toggleSidebar',
  ToggleMainSidebar = 'toggleMainSidebar',
  SetConfig = 'setConfig',
  SetUnauthorized = 'setUnauthorized',
  SetSessionId = 'setSessionId',
  SetPermissionsV3 = 'setPermissionsV3',
}

export type GlobalAction =
  | { type: GlobalActionTypes.ToggleSidebar }
  | { type: GlobalActionTypes.ToggleMainSidebar }
  | { type: GlobalActionTypes.SetUser; payload: User }
  | { type: GlobalActionTypes.SetToken; payload: string }
  | { type: GlobalActionTypes.SetConfig; payload: any }
  | { type: GlobalActionTypes.SetUnauthorized }
  | { type: GlobalActionTypes.SetSessionId; payload: string }
  | { type: GlobalActionTypes.SetPermissionsV3; payload: Set<string> };
type GlobalDispatch = (action: GlobalAction) => void;

const GlobalStateContext = React.createContext<GlobalState | undefined>(
  undefined
);
const GlobalDispatchContext = React.createContext<GlobalDispatch | undefined>(
  undefined
);

function globalReducer(state: GlobalState, action: GlobalAction): GlobalState {
  switch (action.type) {
    case GlobalActionTypes.SetUser: {
      var tempUser = new User();
      Object.assign(tempUser, action.payload);
      return { ...state, currentUser: tempUser, isUnauthorized: false };
    }
    case GlobalActionTypes.SetToken: {
      return { ...state, token: action.payload };
    }
    case GlobalActionTypes.ToggleSidebar: {
      return { ...state, isSidebarOpen: !state.isSidebarOpen };
    }
    case GlobalActionTypes.ToggleMainSidebar: {
      return { ...state, isMainSidebarOpen: !state.isMainSidebarOpen };
    }
    case GlobalActionTypes.SetConfig: {
      return { ...state, ...action.payload };
    }
    case GlobalActionTypes.SetUnauthorized: {
      var user = new User();
      // Clear session ID from local storage when unauthorized
      localStorage.removeItem('sessionId');
      return { ...state, currentUser: user, isUnauthorized: true };
    }
    case GlobalActionTypes.SetSessionId: {
      return { ...state, sessionId: action.payload };
    }
    case GlobalActionTypes.SetPermissionsV3: {
      return { ...state, permissionsV3: action.payload };
    }
    default: {
      throw new Error('Invalid action type');
    }
  }
}

export const getInitialConfig = () => {
  var configObj: GlobalState =
    process.env.NODE_ENV === 'development'
      ? {
          tenant: process.env.REACT_APP_TENANT,
          clientId: process.env.REACT_APP_CLIENT_ID,
          baseUrl: process.env.REACT_APP_BASE_URL || 'localhost:44378',
          redirectUri: process.env.REACT_APP_REDIRECT_URI,
          environmentName: process.env.REACT_APP_ENVIRONMENT_NAME,
          versionNumber: '0.0.0.0',
          isSidebarOpen: true,
          isMainSidebarOpen: true,
        }
      : {
          tenant: window.TENANT,
          clientId: window.CLIENT_ID,
          baseUrl: window.BASE_URL,
          redirectUri: window.REDIRECT_URI,
          versionNumber: window.VERSION_NUMBER,
          environmentName: window.ENVIRONMENT_NAME,
          isSidebarOpen: true,
          isMainSidebarOpen: true,
        };

  return configObj;
};

export const GlobalProvider: React.FC = ({ children }) => {
  const [state, dispatch] = React.useReducer(globalReducer, {
    ...getInitialConfig(),
  });

  return (
    <GlobalStateContext.Provider value={state}>
      <GlobalDispatchContext.Provider value={dispatch}>
        <GlobalProviderContent>{children}</GlobalProviderContent>
      </GlobalDispatchContext.Provider>
    </GlobalStateContext.Provider>
  );
};

export const useLoadPermissionsV3 = () => {
  const dispatch = useGlobalDispatch();
  const API = useApiWorker('3.0');
  const { token } = useGlobalState();

  return React.useCallback(async () => {
    if (isNullEmptyOrUndefined(token)) return;
    try {
      const { data } = await API.get(ENDPOINTS.auth.endpointString('permissions'));
      dispatch({ type: GlobalActionTypes.SetPermissionsV3, payload: new Set<string>(data) });
    } catch (e) {
      console.log(e);
    }
  }, [API, token, dispatch]);
};

export const useRefreshCurrentUser = () => {
  const dispatch = useGlobalDispatch();
  const API = useApiWorker();
  const toast = useToasts();
  const { token } = useGlobalState();

  const refreshCurrentUser = React.useCallback(async () => {
    if (isNullEmptyOrUndefined(token)) return;

    try {
      const { data } = await API.get(ENDPOINTS.users.endpointString('current'));
      dispatch({ type: GlobalActionTypes.SetUser, payload: data });
    } catch (e) {
      console.log(e);
      toast.error('Error refreshing user data');
    }
  }, [API, token, dispatch, toast]);

  return refreshCurrentUser;
};

export const GlobalProviderContent: React.FC = ({ children }) => {
  const { currentUser, isUnauthorized, token } = useGlobalState();
  const dispatch = useGlobalDispatch();
  const toast = useToasts();
  const API = useApiWorker();
  const APIv3 = useApiWorker('3.0');
  const [userError, setUserError] = React.useState(false);
  const { instance } = useMsal();
  const sessionValidationIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const account = instance.getActiveAccount();
  React.useEffect(() => {
    if (account) {
      instance
        .acquireTokenSilent({
          scopes: [
            process.env.NODE_ENV === 'development'
              ? `${process.env.REACT_APP_CLIENT_ID}`
              : `${window.CLIENT_ID}`,
          ],
          account: account,
        })
        .catch((error) => {
          console.error('AQUIRE TOKEN ERROR: ', error);
          if (error instanceof InteractionRequiredAuthError) {
            // fallback to interaction when silent call fails
            console.log('ATTEMPTING REDIRECT TO RESOLVE');
            instance.acquireTokenRedirect({
              scopes: [
                process.env.NODE_ENV === 'development'
                  ? `${process.env.REACT_APP_CLIENT_ID}`
                  : `${window.CLIENT_ID}`,
              ],
              account: account,
            });
          } else {
            instance.logout();
          }
        })
        .then((response) => {
          if (response) {
            dispatch({
              type: GlobalActionTypes.SetToken,
              payload: `${response.accessToken}`,
            });
            
            // Check if sessionId already exists (page refresh)
            let sessionId = localStorage.getItem('sessionId');
            
            // Only generate a new sessionId on actual login, not on page refresh
            if (!sessionId) {
              sessionId = uuidv4();
              localStorage.setItem('sessionId', sessionId);
              // Clear refresh flag on new login
              sessionStorage.removeItem('SessionIdRefreshed');
            } else {
              // Token was refreshed for existing sessionId
              sessionStorage.setItem('SessionIdRefreshed', 'true');
            }
            
            dispatch({ type: GlobalActionTypes.SetSessionId, payload: sessionId });
          }
        });
    }
  }, []);

  React.useEffect(() => {
    if (isNullEmptyOrUndefined(token)) return;

    API.get(ENDPOINTS.users.endpointString('current'))
      .then(({ data }) => {
        dispatch({ type: GlobalActionTypes.SetUser, payload: data });
      })
      .catch(({ response }) => {
        console.log(response);
        if (response?.status === 401) {
          dispatch({ type: GlobalActionTypes.SetUnauthorized });
        } else {
          setUserError(true);
          toast.error('Error loading user data');
        }
      });

    APIv3.get(ENDPOINTS.auth.endpointString('permissions'))
      .then(({ data }) => {
        dispatch({ type: GlobalActionTypes.SetPermissionsV3, payload: new Set<string>(data) });
      })
      .catch((e) => {
        console.log(e);
      });
  }, [token]);

  // Session validation timer - checks every 30 seconds after 30 seconds of component load
  React.useEffect(() => {
    if (isNullEmptyOrUndefined(token)) return;

    const SESSION_VALIDATION_INTERVAL = 10000; // 10 seconds

    const startSessionValidation = () => {
      sessionValidationIntervalRef.current = setInterval(async () => {
        try {
          await API.get(ENDPOINTS.users.endpointString('IsValidSession'));
        } catch (error: any) {
          console.error('Session validation error:', error);
          // The axios interceptors will handle 401 responses and redirect appropriately
        }
      }, SESSION_VALIDATION_INTERVAL);
    };

    // Set initial timeout before starting the interval
    const initialTimeoutId = setTimeout(startSessionValidation, SESSION_VALIDATION_INTERVAL);

    return () => {
      clearTimeout(initialTimeoutId);
      if (sessionValidationIntervalRef.current) {
        clearInterval(sessionValidationIntervalRef.current);
      }
    };
  }, [token, API]);

  return (
    <LoadingSection
      isLoading={
        (token === undefined || currentUser === undefined) &&
        !userError &&
        !isUnauthorized
      }
      loadingText="Initializing User Session..."
      wrapperClass="full-view-height"
    >
      {isUnauthorized || userError ? (
        <Layout>
          <GlobalErrorDisplay
            userError={userError}
            isUnauthorized={isUnauthorized}
          />
        </Layout>
      ) : (
        children
      )}
    </LoadingSection>
  );
};

interface GlobalErrorDisplayProps {
  userError?: boolean;
  isUnauthorized?: boolean;
  loginError?: boolean;
  message?: any;
}

export const GlobalErrorDisplay = ({
  userError,
  isUnauthorized,
  loginError,
  message,
}: GlobalErrorDisplayProps) => {
  const { instance } = useMsal();
  if (message?.error) {
    console.error(message?.error);
  }
  const errorMessage = loginError
    ? 'Error logging in'
    : isUnauthorized
    ? 'You Are Not Authorized to View This Content'
    : 'Error Loading User Data';

  const userLogOut = async () => {
    // add api at the start of the logout call
    const logoutUrl = 'api/' + ENDPOINTS.users.endpointString('logout');
    await asyncLogout(logoutUrl, localStorage.getItem('sessionId'));
    localStorage.removeItem('sessionId');
    instance.logout();
  };

  return (
    <PageCard width={600}>
      <PageCardRowSection>
        <div className="flex-center flex-column px-large py-large">
          <ErrorIcon
            className="color-danger mb-regular"
            style={{ width: 50, height: 50 }}
          />
          <h2 className="pb-regular">{errorMessage}</h2>
          {!isUnauthorized && (
            <span className="color-secondary">Try refreshing the page</span>
          )}
          <Link to={'#'} onClick={userLogOut} className={'pt-2'}>
            <ExitToAppIcon className="mr-small color-primary" />
            Log Out
          </Link>
        </div>
      </PageCardRowSection>
    </PageCard>
  );
};

export const useGlobalState = () => {
  const globalStateContext = React.useContext(GlobalStateContext);
  if (globalStateContext === undefined) {
    throw new Error('useGlobalState must be used within a GlobalProvider');
  }
  return globalStateContext;
};

export const useGlobalDispatch = () => {
  const globalDispatchContext = React.useContext(GlobalDispatchContext);
  if (globalDispatchContext === undefined) {
    throw new Error('useGlobalDispatch must be used within a GlobalProvider');
  }
  return globalDispatchContext;
};

export const useGlobalContext = () => {
  const state = useGlobalState();
  const dispatch = useGlobalDispatch();

  return [state, dispatch] as const;
};
